/**
 * Reads the real error message out of a failed HIL-submit response instead of discarding it.
 *
 * Root-caused 2026-09-12: ChatDesk's "Failed to send reply: HTTP 400" told the user nothing —
 * both `FetchHilSubmitClient` and `createBearerTokenSubmitClient` built `error: \`HTTP ${status}\``
 * on a non-ok response without ever reading the body, so `BaseHilRespondController`'s real
 * `{ error: "..." }` payload (e.g. "session not found or already completed") never reached the UI
 * or the caller's own error handling — every failure looked identical regardless of cause.
 *
 * Every server error path in this contract (`BaseHilRespondController.Respond`, and every other
 * `[ApiController]`-style controller in this codebase) returns `{ error: string }` JSON on failure;
 * falls back to raw response text for anything else (a non-JSON 4xx/5xx from a proxy, gateway, or
 * unhandled exception page), then a generic message if the body is empty/unreadable.
 */
export async function parseHilSubmitError(response: Response): Promise<string> {
  let bodyText: string;
  try {
    bodyText = await response.text();
  } catch {
    return `HTTP ${response.status}`;
  }

  if (bodyText) {
    try {
      const parsed = JSON.parse(bodyText) as { error?: unknown; message?: unknown };
      const message = parsed.error ?? parsed.message;
      if (typeof message === 'string' && message.length > 0) return message;
    } catch {
      // Not JSON — fall through to the raw text below.
    }
    return bodyText;
  }

  return `HTTP ${response.status}`;
}
